import styled from 'styled-components';
import React, { createContext, FC, ReactText, useCallback } from 'react';
import { replace } from 'redux-first-history';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Grid } from '@material-ui/core';

import { addParamToUrl } from '@api/services/tools/handleUrlParams';
import { LogTypesString } from '@api/store/logs/types';
import { useMobile } from '@api/services/hooks/useMobile';
import { debounce } from '@api/services/tools/debounce';

import { CampaignFilter } from './components/logFilters/CampaignFilter';
import { ChannelFilter } from './components/logFilters/ChannelFilter';
import { MobileLogsFilterBar } from './components';
import { DateFilter } from './components/logFilters/DateFilter';
import { SearchFilter } from './components/logFilters/SearchFilter';
import { ClearChannels, getChannelsStore, LoadChannelsStart } from '@api/store/channels/actions';
import { INITIAL_PER_PAGE } from '@api/store/exports/types';
import { useDateParamsBasedOnLogsRetention } from '../../hooks/useDateParamsBasedOnLogsRetention';

const OptionBarPadding = styled.div`
    padding: 0 48px;
`;
const TopBarOptions = styled(Grid)`
    padding: 24px 0px;
    & .top-bar--options > * {
        padding-right: 24px;
    }
    & div:not(.bottomPanel) > .MuiButton-root {
        padding-left: 0px;
        &:hover,
        &:focus {
            padding-left: 0px;
        }
    }
`;

export interface LogsFiltersBarInterface {
    selectedChannel?: string;
    selectedCampaign?: string;
    onlyLastEvent?: boolean;
    emailSearch?: string;
    dateFilterValue?: ReactText;
    logTypeScreen?: LogTypesString;
    onClicks?: {
        refresh?(): void;
        campaignFilter?(event: React.MouseEvent<HTMLInputElement>, channel: { value: string; label: string }): void;
        channelFilter?(event: React.MouseEvent<HTMLInputElement>, channel: { value: string; label: string }): void;
        calendar?(fromTime: Date, toTime: Date): void;
        dateFilter?(event: any, selectedItem): void;
        search?(emailSearch?: string): void;
        lastEvent?(ev: React.ChangeEvent<HTMLInputElement>): void;
    };
    onFilterChange?(updatedFilters: Record<string, any>): void;
    onMobileFilterChange?(updatedFilters: Record<string, any>): void;
    availableFilters?: {
        date?: boolean;
        channel?: boolean;
        campaign?: boolean;
        search?: boolean;
        lastEvent?: boolean;
    };
}

export interface FilterContextInterface extends Pick<LogsFiltersBarInterface, 'onClicks' | 'availableFilters'> {
    updateFilters?(filtersToAdd: Record<string, any>): void;
    logScreenType?: LogTypesString;
    fetchNextChannels?(search?: string, customOffset?: number): void;
}

export const GlobalFilterOptionsContext = createContext<FilterContextInterface>(null);

export const LogsFiltersBar: FC<LogsFiltersBarInterface> = ({
    selectedChannel,
    selectedCampaign,
    emailSearch,
    dateFilterValue,
    onClicks,
    onFilterChange,
    onMobileFilterChange,
    availableFilters,
    logTypeScreen,
}: LogsFiltersBarInterface): JSX.Element => {
    const dispatch = useDispatch();
    const { logType } = useParams<{ logType: LogTypesString }>();
    const { from, to } = useDateParamsBasedOnLogsRetention(true);
    const isMobile = useMobile();
    const { channels, hasMore } = useSelector(getChannelsStore);
    const updateFilters = (filtersToUpdate: Record<string, any>): void => {
        const urlParams = {
            ...filtersToUpdate,
            ...(isMobile && { page: 1 }),
        };
        const newPath = addParamToUrl(urlParams);
        dispatch(replace({ search: newPath }));
        debounce(() => (isMobile ? onMobileFilterChange?.(filtersToUpdate) : onFilterChange?.(filtersToUpdate)), 500);
    };

    const fetchNextChannels = useCallback(
        (search: string, customOffset?: number) => {
            const offset = customOffset ?? channels?.length;
            !offset && dispatch(ClearChannels());
            (hasMore || !offset) && dispatch(LoadChannelsStart({ limit: INITIAL_PER_PAGE, offset, search, skipSagaURL: true, from, to }));
        },
        [channels?.length]
    );

    return (
        <GlobalFilterOptionsContext.Provider
            value={{ onClicks, updateFilters, logScreenType: logTypeScreen || logType, availableFilters, fetchNextChannels }}
        >
            {isMobile ? (
                <MobileLogsFilterBar onChangeFilters={updateFilters} availableFilters={availableFilters} />
            ) : (
                <OptionBarPadding>
                    <TopBarOptions container alignItems="center">
                        <Grid item xs={12} sm={8}>
                            <Grid container direction="row" alignItems="center" className="top-bar--options">
                                <DateFilter dateFilterValue={dateFilterValue} />
                                <ChannelFilter selectedChannel={selectedChannel} />
                                <CampaignFilter selectedCampaign={selectedCampaign} />
                            </Grid>
                        </Grid>
                        <SearchFilter emailSearch={emailSearch} />
                    </TopBarOptions>
                </OptionBarPadding>
            )}
        </GlobalFilterOptionsContext.Provider>
    );
};
